import type { ReactNode } from "react";
import { Badge } from "../atoms/Badge";
import { Hash } from "../atoms/Hash";
import { Heading } from "../atoms/Heading";
import { KeyValue } from "../atoms/KeyValue";
import { cn } from "../lib/cn";
import { CopyButton } from "../molecules/CopyButton";
import type { BatchRow } from "./BatchTable";

export interface BatchSummaryCardProps extends BatchRow {
	className?: string;
}

function BatchSummaryCard({
	merkleRoot,
	recordCount,
	createdAt,
	anchored,
	chainName,
	txHash,
	blockNumber,
	blockTimestamp,
	className,
}: BatchSummaryCardProps): ReactNode {
	return (
		<div
			className={cn(
				"flex flex-col gap-3 rounded-lg border border-border bg-card p-4",
				className,
			)}
		>
			<Heading size="sm">Record details</Heading>
			<div className="flex flex-wrap gap-4">
				<KeyValue
					label="Events"
					value={
						!anchored && recordCount === 0 ? "Pending" : String(recordCount)
					}
					direction="horizontal"
				/>
				<KeyValue label="Created" value={createdAt} direction="horizontal" />
				{chainName && (
					<KeyValue
						label="Chain"
						value={<Badge variant="outline">{chainName}</Badge>}
						direction="horizontal"
					/>
				)}
				{txHash && (
					<KeyValue
						label="Tx Hash"
						value={
							<div className="flex items-center gap-1">
								<Hash value={txHash} length={4} />
								<CopyButton value={txHash} />
							</div>
						}
						direction="horizontal"
					/>
				)}
				{blockNumber != null && (
					<KeyValue
						label="Block"
						value={String(blockNumber)}
						direction="horizontal"
					/>
				)}
				{blockTimestamp && (
					<KeyValue
						label="Block time"
						value={blockTimestamp}
						direction="horizontal"
					/>
				)}
			</div>
			<div className="flex flex-col gap-1 border-t border-border pt-3">
				<span className="text-xs font-medium text-mutedForeground">
					Technical reference
				</span>
				<div className="flex items-center gap-1">
					<Hash value={merkleRoot} />
					<CopyButton value={merkleRoot} />
				</div>
			</div>
		</div>
	);
}
BatchSummaryCard.displayName = "BatchSummaryCard";

export default BatchSummaryCard;
export { BatchSummaryCard };
